import { useState } from 'react';
import { useQuery } from '../hooks/useQuery';
import { useAuth } from '../hooks/useAuth';
import { api, type GlReconStatus } from '../lib/api';
import { Card, CardHeader, Table, Th, Tr, Td, Badge, ActionButton, Spinner, EmptyState } from '../components/ui';
import { fmtCurrencyWhole } from '../lib/fmt';

const inputStyle: React.CSSProperties = {
  fontSize: 11, padding: '3px 6px', border: '0.5px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)',
};
const RECON_VARIANT: Record<GlReconStatus, 'green' | 'blue' | 'red'> = { matched: 'green', reconciled: 'blue', unexplained: 'red' };
function prevMonth() { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; }

export default function AdminFpaClosePage() {
  const { user } = useAuth();
  const canWrite = user?.fpa_role === 'analyst' || user?.fpa_role === 'approver';
  const isApprover = user?.fpa_role === 'approver';
  const [month, setMonth] = useState(prevMonth());

  const closeQ = useQuery(() => api.fpa.close(month), [month]);
  const reconQ = useQuery(() => api.fpa.glRecon(month), [month]);
  const [task, setTask] = useState('');
  const [item, setItem] = useState({ amount: '', description: '' });

  async function toggleStep(step: string, complete: boolean) { await api.fpa.completeCloseStep(month, step, complete); closeQ.refetch(); }
  async function addTask() { if (!task.trim()) return; await api.fpa.addChecklistItem(month, { task_name: task }); setTask(''); closeQ.refetch(); }
  async function toggleTask(tid: string, completed: boolean) { await api.fpa.updateChecklistItem(month, tid, { completed }); closeQ.refetch(); }
  async function addReconItem() { if (item.amount === '') return; await api.fpa.addReconItem(month, { amount: Number(item.amount), description: item.description }); setItem({ amount: '', description: '' }); reconQ.refetch(); }
  async function sign() { await api.fpa.signOffRecon(month); reconQ.refetch(); }

  const recon = reconQ.data?.recon;

  return (
    <>
      <Card>
        <CardHeader>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Period close</div>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Period</span>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={inputStyle} />
          </span>
        </CardHeader>
        <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--color-text-muted)' }}>
          Monitoring &amp; hygiene over QBO — not an accounting close (the ledger lives in QuickBooks).
        </div>
      </Card>

      {/* GL tie-out */}
      <Card>
        <CardHeader><div style={{ fontSize: 13, fontWeight: 500 }}>GL tie-out (revenue)</div></CardHeader>
        {reconQ.isLoading || !recon ? <Spinner /> : (
          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12 }}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>QBO recognised: <strong>{fmtCurrencyWhole(recon.gl_amount)}</strong></span>
              <span>ChargeOver billed: <strong>{fmtCurrencyWhole(recon.fpa_amount)}</strong></span>
              <span>Δ: <strong style={{ color: recon.delta === 0 ? 'var(--color-text)' : 'var(--color-red-text)' }}>{fmtCurrencyWhole(recon.delta)}</strong></span>
              <Badge variant={RECON_VARIANT[recon.status]}>{recon.status}</Badge>
              {recon.signed_off_by && <span style={{ color: 'var(--color-text-muted)' }}>signed off by {recon.signed_off_by}</span>}
              {isApprover && !recon.signed_off_by && <ActionButton variant="primary" onClick={sign}>Sign off</ActionButton>}
            </div>
            {(recon.reconciling_items ?? []).length > 0 && (
              <div>
                {recon.reconciling_items!.map((ri, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '0.5px solid var(--color-border)', padding: '3px 0' }}>
                    <span>{ri.description || '—'}</span><span>{fmtCurrencyWhole(ri.amount)}</span>
                  </div>
                ))}
              </div>
            )}
            {canWrite && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="number" placeholder="amount" value={item.amount} onChange={(e) => setItem({ ...item, amount: e.target.value })} style={{ ...inputStyle, width: 110 }} />
                <input placeholder="reconciling note" value={item.description} onChange={(e) => setItem({ ...item, description: e.target.value })} style={{ ...inputStyle, flex: 1 }} />
                <ActionButton onClick={addReconItem}>Add item</ActionButton>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Close calendar */}
      <Card>
        <CardHeader><div style={{ fontSize: 13, fontWeight: 500 }}>Close calendar</div></CardHeader>
        {closeQ.isLoading ? <Spinner /> : (
          <Table>
            <thead><tr><Th>Step</Th><Th>Owner</Th><Th>Target</Th><Th>Status</Th></tr></thead>
            <tbody>
              {(closeQ.data?.calendar ?? []).map((s) => (
                <Tr key={s.id}>
                  <Td bold>{s.step_name}</Td>
                  <Td muted>{s.owner_role ?? '—'}</Td>
                  <Td muted>{s.target_date ?? '—'}</Td>
                  <Td>
                    {s.completed_at
                      ? <Badge variant="green">done</Badge>
                      : <Badge variant="gray">pending</Badge>}
                    {canWrite && <span style={{ marginLeft: 6 }}><ActionButton onClick={() => toggleStep(s.step_code, !s.completed_at)}>{s.completed_at ? 'Reopen' : 'Complete'}</ActionButton></span>}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {/* Checklist */}
      <Card>
        <CardHeader><div style={{ fontSize: 13, fontWeight: 500 }}>Checklist</div></CardHeader>
        {canWrite && (
          <div style={{ padding: '10px 16px', borderBottom: '0.5px solid var(--color-border)', display: 'flex', gap: 6 }}>
            <input placeholder="Add a task…" value={task} onChange={(e) => setTask(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
            <ActionButton variant="primary" disabled={!task.trim()} onClick={addTask}>Add</ActionButton>
          </div>
        )}
        {closeQ.isLoading ? <Spinner />
          : (closeQ.data?.checklist ?? []).length === 0 ? <EmptyState message="No checklist items." />
          : (
            <Table>
              <thead><tr><Th>Task</Th><Th>Status</Th></tr></thead>
              <tbody>
                {closeQ.data!.checklist.map((t) => (
                  <Tr key={t.id}>
                    <Td bold>{t.task_name}</Td>
                    <Td>
                      {t.completed_at ? <Badge variant="green">done</Badge> : <Badge variant="gray">open</Badge>}
                      {canWrite && <span style={{ marginLeft: 6 }}><ActionButton onClick={() => toggleTask(t.id, !t.completed_at)}>{t.completed_at ? 'Reopen' : 'Done'}</ActionButton></span>}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
      </Card>
    </>
  );
}
